// BWM UTM Convention v2 — aligned to GA4's Default Channel Group rules.
//
// Why this layout:
//   utm_source  = WHERE the click came from (the asset/site).
//   utm_medium  = the CHANNEL category. GA4 uses this to bucket traffic
//                 into Default Channel Groups (Organic, Email, Social,
//                 Referral, etc.). Putting placement values here
//                 (cta_block, footer, hero) sends everything to
//                 "Unassigned" — that's why those values now live in
//                 utm_term.
//   utm_campaign = kebab-case identifier (post slug or campaign name).
//   utm_content  = optional, the specific asset variant.
//   utm_term     = the placement on the page (cta-block, footer, etc.).
//
// Convention: dashes everywhere, no underscores. GA4 treats `case_study`
// and `case-study` as different sources — we only ever want one.

export const UTM_SOURCES = [
  { id: 'blog',            label: 'Blog' },
  { id: 'case-study',      label: 'Case Study' },
  { id: 'landing',         label: 'Landing Page' },
  { id: 'newsletter',      label: 'Newsletter' },
  { id: 'youtube',         label: 'YouTube' },
  { id: 'linkedin',        label: 'LinkedIn' },
  { id: 'email-signature', label: 'Email Signature' },
];

// GA4 Default Channel Group categories. These map cleanly to GA4's
// built-in channel rules. `content` lands in "Unassigned" by default —
// customize the Channel Group in GA4 to bucket it under a "Content
// Marketing" channel if you want a single rollup.
export const UTM_MEDIUMS = [
  { id: 'content',  label: 'Content Marketing' },
  { id: 'email',    label: 'Email' },
  { id: 'social',   label: 'Organic Social' },
  { id: 'referral', label: 'Referral' },
  { id: 'paid',     label: 'Paid' },
  { id: 'organic',  label: 'Organic Search' },
];

// Placement on the page. Goes in utm_term so we can slice traffic by
// "where on the page did the click happen" without polluting medium.
export const UTM_TERMS = [
  { id: '',            label: '— None —' },
  { id: 'cta-block',   label: 'CTA Block' },
  { id: 'inline-link', label: 'Inline Link' },
  { id: 'footer',      label: 'Footer' },
  { id: 'sidebar',     label: 'Sidebar' },
  { id: 'hero',        label: 'Hero' },
  { id: 'nav',         label: 'Nav' },
];

// Validates a campaign or content slug against kebab-case rules:
// lowercase letters, digits, hyphens. No leading/trailing hyphen.
export function isValidKebab(s) {
  if (!s) return false;
  return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(s);
}

// Normalize freeform text to kebab-case (best-effort, for the auto-fill from
// post slug). Drops anything that isn't [a-z0-9-].
export function toKebab(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

// Build the final URL with proper encoding. Preserves any existing query
// string on the base URL.
export function buildUtmUrl({ baseUrl, utmSource, utmMedium, utmCampaign, utmContent, utmTerm }) {
  if (!baseUrl) return '';
  let url;
  try {
    url = new URL(baseUrl);
  } catch {
    return ''; // invalid base URL
  }
  url.searchParams.set('utm_source', utmSource);
  url.searchParams.set('utm_medium', utmMedium);
  url.searchParams.set('utm_campaign', utmCampaign);
  if (utmContent) url.searchParams.set('utm_content', utmContent);
  if (utmTerm) url.searchParams.set('utm_term', utmTerm);
  return url.toString();
}
